import * as React from 'react';
import { cn } from '../../lib/cn';
import { sanitizeNumericText } from '../../lib/numeric_text';
import { AutoGrowTextarea } from '../auto_grow_textarea/auto_grow_textarea';

export interface MoneyInputBaseProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  // `size` (the HTML character-width attribute) is dropped for the same reason
  // `<Input>` drops it: it does nothing useful on a styled field, and it would
  // collide with `<AutoGrowTextarea>`'s own `size` height variant under
  // `autoGrow`. No call site passed it. `onBlur` is dropped from the
  // inherited HTML attribute type and re-declared explicitly below: the
  // component's OWN blur handler owns the canonical-format reformat (deferred
  // from onChange), so a caller-supplied `onBlur` must be composed with it
  // rather than silently overriding it via prop spread order. `onChange` is
  // dropped and re-declared per variant below (see the discriminated union).
  'type' | 'onChange' | 'value' | 'size' | 'onBlur'
> {
  /** Value in minor units (cents). */
  value: number | undefined;
  /**
   * Fires after the component's own blur-time reformat (canonical `0.00`
   * snap, skipped under `freeFormat`). For callers that need to react to the
   * field losing focus — e.g. deciding whether an empty/cleared value should
   * resume some other auto-fill behaviour — without reimplementing the
   * reformat themselves.
   */
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  /** Number of decimal places for the currency (default 2). */
  decimals?: number;
  /** Currency symbol to display (e.g., "$", "Rs."). */
  symbol?: string;
  error?: boolean;
  /**
   * When true, a zero value renders as an empty field (so the user can type a
   * value without first clearing a pre-filled `0.00`). The formatted zero is
   * still surfaced via the placeholder. Defaults to false to preserve the
   * explicit `0.00` display used elsewhere (totals, read-only amounts).
   */
  blankZero?: boolean;
  /**
   * When true, the field keeps exactly what the user typed (e.g. `58`, `58.5`)
   * and does NOT reformat to a fixed-decimal string (`58.00`) on blur. The
   * stored value (cents) is still computed correctly from the typed text.
   * Defaults to false so totals/read-only amounts keep canonical `0.00`
   * formatting.
   */
  freeFormat?: boolean;
  /**
   * Render the field as an auto-growing cell (`<AutoGrowTextarea>`) instead of
   * a fixed single-line `<input>`: a long amount wraps onto another line and
   * grows the row rather than scrolling out of view. For money columns in a
   * line-item grid.
   *
   * Everything about the value stays as it is — the display↔minor-units
   * conversion, `blankZero`, `freeFormat`, select-on-focus and the blur
   * reformat are shared by both renderings. Only the element changes, which is
   * why this is a prop here rather than an `<AutoGrowTextarea numeric>` at the
   * call site: a call site that swapped the element itself would have to
   * re-implement the cents conversion, and money parsing lives in exactly one
   * place (docs/money-rules.md).
   */
  autoGrow?: boolean;
}

/**
 * Default (non-empty) variant. `onChange` always fires a concrete `number`,
 * and an empty/cleared field emits `0` — the historical behaviour every
 * existing call site relies on. This is the variant TypeScript selects when
 * `allowEmpty` is omitted, so no call site changes.
 */
export interface MoneyInputNumberProps extends MoneyInputBaseProps {
  allowEmpty?: false;
  /** Called with new value in minor units (cents). */
  onChange: (cents: number) => void;
}

/**
 * Opt-in empty-capable variant. When `allowEmpty` is `true` the field can
 * represent "no value at all": a cleared field emits `undefined` (NOT `0`), so
 * a caller can round-trip a nullable amount back to null. Use this for the one
 * kind of field where zero and not-captured are genuinely different states
 * (e.g. an optional regulatory figure that skips a cross-check when blank).
 * Pair it with a distinct `placeholder` — never `blankZero` — so the empty
 * state is visually distinguishable from a stored `0`.
 */
export interface MoneyInputEmptyProps extends MoneyInputBaseProps {
  allowEmpty: true;
  /** Called with new value in minor units, or `undefined` when cleared. */
  onChange: (cents: number | undefined) => void;
}

/** Public props: the non-empty variant by default, opt into empty-capable. */
export type MoneyInputProps = MoneyInputNumberProps | MoneyInputEmptyProps;

/**
 * Emit a value to the caller's `onChange`, narrowing on the `allowEmpty`
 * discriminant. Kept as a free function taking the whole props union so the
 * narrowing survives — destructuring `onChange`/`allowEmpty` into locals would
 * sever the correlation TypeScript needs to accept `onChange(undefined)`. In
 * the non-empty variant a would-be `undefined` collapses to `0`, which the
 * ternary in `handleChange` never actually produces, keeping behaviour
 * byte-identical for existing callers.
 */
function emitMoneyChange(
  props: MoneyInputProps,
  cents: number | undefined,
): void {
  if (props.allowEmpty) {
    props.onChange(cents);
  } else {
    props.onChange(cents ?? 0);
  }
}

// Grouping separators only ever appear in the settled display (initial
// render, external value sync, blur reformat) — never while the user is
// mid-keystroke, since `handleChange` writes the sanitized raw text
// directly. `displayToCents` strips anything that isn't a digit, dot, or
// minus, so a comma-grouped string round-trips through it unchanged.
function centsToDisplay(
  cents: number | undefined,
  decimals: number,
  blankZero = false,
): string {
  if (cents === undefined) return '';
  if (blankZero && cents === 0) return '';
  const divisor = Math.pow(10, decimals);
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(cents / divisor);
}

function displayToCents(display: string, decimals: number): number {
  const cleaned = display.replace(/[^0-9.-]/g, '');
  if (cleaned === '' || cleaned === '-') return 0;
  const num = parseFloat(cleaned);
  if (isNaN(num)) return 0;
  const divisor = Math.pow(10, decimals);
  return Math.round(num * divisor);
}

export const MoneyInput = React.forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  MoneyInputProps
>(function MoneyInput(props, ref) {
  const {
    value,
    // `onChange` is intentionally not destructured here — `emitMoneyChange`
    // reads it off `props` so the `allowEmpty` discriminant narrows it. It is
    // named with a leading underscore only to keep it out of `rest` (which is
    // spread onto the element); the value is never read through this local.
    onChange: _onChange,
    onBlur: onBlurProp,
    allowEmpty = false,
    decimals = 2,
    symbol,
    error,
    blankZero = false,
    freeFormat = false,
    autoGrow = false,
    className,
    placeholder,
    ...rest
  } = props;

  const [display, setDisplay] = React.useState(() =>
    centsToDisplay(value, decimals, blankZero),
  );

  React.useEffect(() => {
    setDisplay((prev) => {
      // Only sync when the stored value changed to something the current
      // display no longer represents.  Formatting normalisation (e.g. "5" →
      // "5.00") is deferred to onBlur so the user can type freely mid-number.
      if (displayToCents(prev, decimals) !== value) {
        return centsToDisplay(value, decimals, blankZero);
      }
      return prev;
    });
  }, [value, decimals, blankZero]);

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    // Number-only: drop any non-numeric characters as they are typed.
    const raw = sanitizeNumericText(e.target.value);
    setDisplay(raw);
    // In empty-capable mode a field with no digits at all is "not captured" and
    // must emit `undefined`, never `0` — otherwise clearing the field would be
    // indistinguishable from typing a zero. Every other case (and every
    // non-`allowEmpty` field) emits the parsed minor-unit number as before.
    const next =
      allowEmpty && !/[0-9]/.test(raw)
        ? undefined
        : displayToCents(raw, decimals);
    emitMoneyChange(props, next);
  }

  function handleBlur(
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    // Free-format: keep the typed text as-is (do not snap to `0.00`).
    if (!freeFormat) {
      setDisplay(centsToDisplay(value, decimals, blankZero));
    }
    onBlurProp?.(e);
  }

  function handleFocus(
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    const target = e.target;
    setTimeout(() => {
      target.select();
    }, 0);
  }

  const resolvedPlaceholder =
    placeholder ?? (blankZero ? (0).toFixed(decimals) : undefined);

  // The pass-through attributes, retyped against the element the auto-growing
  // rendering actually uses. Every attribute callers pass here (id, name,
  // disabled, tabIndex, aria-*, data-*, onKeyDown, onFocus…) exists on both
  // elements and behaves identically; only the event handlers' element
  // generic differs, so this is the external-type-mismatch case a named
  // assertion is for — not a way to silence a real incompatibility.
  const textareaProps =
    rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>;

  return (
    // Amounts read left-to-right in every locale (ADR-021 § 8). `dir` lives on
    // the WRAPPER, not the input: the symbol (`start-3`), the `ps-8` gutter and
    // `text-end` must all resolve in the same direction, or under an RTL page
    // (dv-MV) the symbol lands on the right while the gutter stays on the left
    // and the amount overlaps it.
    <div className="relative" dir="ltr">
      {symbol ? (
        <span className="absolute start-3 top-1/2 -translate-y-1/2 text-sm text-[var(--ink-faint)]">
          {symbol}
        </span>
      ) : null}
      {autoGrow ? (
        <AutoGrowTextarea
          ref={ref as React.Ref<HTMLTextAreaElement>}
          // `md` is the 2.25rem collapsed height, matching the `sm:h-9` of the
          // `<input>` rendering below; `nv-input-autogrow-touch` restores that
          // rendering's taller mobile target, which a fixed `h-11` cannot do
          // here (the height is computed from the content).
          size="md"
          numeric
          value={display}
          placeholder={resolvedPlaceholder}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          error={error}
          className={cn(
            'nv-input-autogrow-touch w-full text-base sm:text-[13px] disabled:cursor-not-allowed disabled:opacity-50',
            symbol && 'ps-8',
            className,
          )}
          {...textareaProps}
        />
      ) : (
        <input
          ref={ref as React.Ref<HTMLInputElement>}
          type="text"
          inputMode="decimal"
          value={display}
          placeholder={resolvedPlaceholder}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          className={cn(
            // nv-input handles border, radius, shadow, :focus state (matches Input component).
            // Mobile: h-11 + text-base prevents iOS zoom on focus. sm:+ collapses to h-9 + 13px.
            'nv-input flex h-11 w-full text-end text-base sm:h-9 sm:text-[13px] disabled:cursor-not-allowed disabled:opacity-50',
            symbol && 'ps-8',
            error && 'nv-field-error',
            className,
          )}
          {...rest}
        />
      )}
    </div>
  );
});
